import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { authService } from '@services/auth.service';
import { AuthLinkProblem } from '@components/auth/AuthLinkProblem';

/**
 * Landing page for the SAML SSO redirect.
 *
 * auth-service validated the IdP assertion and handed the browser a single-use
 * handoff code — never a token. We trade that code for a real session (opaque
 * reference tokens, same as password login) and then continue into the app.
 *
 * The code dies on first use and expires in a minute, so this must run exactly
 * once: React 18 StrictMode double-invokes effects in development, and a second
 * exchange would fail against an already-burnt code.
 */
export default function SsoCallbackPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const toast = useToast();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    if (!code) {
      setError('This sign-in link is missing its code. Please start again from the login page.');
      return;
    }

    // Only ever a path — auth-service strips anything absolute or off-site
    // before it reaches us, and we re-check rather than trust the query string.
    const requested = searchParams.get('returnTo') || '/dashboard';
    const returnTo = requested.startsWith('/') && !requested.startsWith('//')
      ? requested
      : '/dashboard';

    (async () => {
      try {
        const session = await authService.exchangeSsoCode(code);
        const user = session.user as any;

        login(
          {
            id: user.id,
            email: user.email,
            name: user.name || user.displayName ||
                  `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
            avatarUrl: user.avatarUrl,
            role: user.role,
            tenantId: user.tenantId,
          },
          session.accessToken,
        );

        toast.success('Welcome back!', `Signed in as ${user.name || user.email}`);
        navigate(returnTo, { replace: true });
      } catch {
        // Covers an expired code, a replayed code, and a deactivated account.
        setError('This sign-in link has expired or has already been used. Please sign in again.');
      }
    })();
    // Intentionally run once on mount; the guard above enforces it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return <AuthLinkProblem title="This sign-in link doesn't work" message={error} />;
  }

  return (
    <div className="mt-8 flex flex-col items-center justify-center gap-3 py-12">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary-600 dark:border-gray-700 dark:border-t-primary-400"
        role="status"
        aria-label="Completing sign-in"
      />
      <p className="text-sm text-gray-500 dark:text-gray-400">Completing sign-in…</p>
    </div>
  );
}
